import { type GlobalSearchItemMetadata } from '../../../value-objects/GlobalSearchItemMetadata'
import {
  GlobalSearchRecentItem,
  type GlobalSearchRecentItemConstructor,
} from '../../../value-objects/GlobalSearchRecentItem'
import { type IGlobalSearchRecentsRepository } from '../../interfaces/IGlobalSearchRecentsRepository'

const MAX_RECENTS = 6

export type AddRecentInput = Pick<
  GlobalSearchRecentItemConstructor,
  'type' | 'uuid' | 'id' | 'metadata'
>

interface Dependency {
  recentsRepository: IGlobalSearchRecentsRepository
}

export class AddGlobalSearchRecentItemUseCase {
  constructor(private readonly deps: Dependency) {}

  /**
   * Strips PII fields (amount) from metadata before persisting.
   * Callers pass metadata as-is; the use case owns the persistence boundary.
   */
  execute = (input: AddRecentInput): void => {
    const sanitizedInput = {
      ...input,
      metadata: this.stripSensitiveFields(input.metadata),
    }

    const now = new Date().toISOString()
    const currentRecents = this.deps.recentsRepository
      .getAll()
      .filter((item) => !item.isExpired)

    const existingIndex = currentRecents.findIndex(
      (item) => item.uuid === sanitizedInput.uuid
    )

    let updatedRecents: GlobalSearchRecentItem[]

    if (existingIndex >= 0) {
      const existing = currentRecents[existingIndex]
      updatedRecents = [
        new GlobalSearchRecentItem({
          ...existing,
          ...sanitizedInput,
          lastAccessedAt: now,
        }),
        ...currentRecents.filter((_, i) => i !== existingIndex),
      ]
    } else {
      updatedRecents = [
        new GlobalSearchRecentItem({
          ...sanitizedInput,
          lastAccessedAt: now,
        }),
        ...currentRecents,
      ]
    }

    this.deps.recentsRepository.save(updatedRecents.slice(0, MAX_RECENTS))
  }

  private stripSensitiveFields(
    metadata: GlobalSearchItemMetadata
  ): GlobalSearchItemMetadata {
    if ('amount' in metadata) {
      const { amount: _amount, ...rest } = metadata
      return rest as GlobalSearchItemMetadata
    }
    return metadata
  }
}
